<template>
  <el-card shadow="never">
     <el-descriptions title="Documents" :colon="false" :column="1" size="medium" border>
       <template slot="extra">
          <el-popover
            placement="top"
            trigger="hover"
            content="FedEx Document Label"
          >
            <el-button
              slot="reference"
              type="danger"
              size="medium"
              icon="el-icon-plus"
              @click="createFedExLabel"
              :loading="shipLabelButtonLoading"
            >
              <el-image :style="{ width: '53px' }" src="https://developer.fedex.com/api/content/dam/fedex-com/irc/leftnav/white.png" fit></el-image>
            </el-button>
          </el-popover>
          <el-button
            @click="() => checkForActiveLease()"
            type="primary"
            size="medium"
            icon="el-icon-plus"
            :disabled="loading && !ready"
            circle
            >
          </el-button>
       </template>
      </el-descriptions>
      <el-tabs v-model="activeTab">
        <el-tab-pane label="Active" name="active">
          <el-table
            :data="[...customerDocs].reverse()"
            v-loading="loading"
            :default-sort = "{prop: 'createdAt', order: 'descending'}"
          >
            <el-table-column sortable label="Created At" prop="createdAt" :formatter="(row, column, cellValue) => $options.filters.formatDate(cellValue)">
            </el-table-column>
            <el-table-column label="Type Of Document" prop="type">
            </el-table-column>
            <el-table-column label="Document">
              <template slot-scope="scope">
                <el-button
                  v-show="scope.row && scope.row.url"
                  size="small"
                  type="primary"
                  icon="el-icon-view"
                  @click="() => openPreview(scope.row)"
                  >
                </el-button>
                <a
                  v-if="scope.row.url"
                  :href="scope.row.url"
                  download
                  slot="reference"
                  >
                    <el-button
                      size="small"
                      type="primary"
                      icon="el-icon-download"
                    >
                    </el-button>
                  </a>
                <el-tag type="warning" v-if="scope.row.type === 'CREDIT_CARD_AUTHORIZATION' && scope.row && scope.row.statusData && scope.row.statusData.status === 'sent'" :style="{ marginLeft: '10px', marginRight: '10px' }">
                CC Form Pending
                </el-tag>
                <el-tag v-if="scope.row.type === 'CREDIT_CARD_AUTHORIZATION' && scope.row && scope.row.statusData && scope.row.statusData.status === 'completed'" type="success" hit :style="{ marginLeft: '10px', marginRight: '10px' }">
                  CC Form on File
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
        <el-tab-pane label="Archived" name="archived">
          <el-table :data="rsCustomer && rsCustomer.archivedDocs ? rsCustomer.archivedDocs : []">
            <el-table-column property="expiry" label="Expired At" sortable>
              <template slot-scope="scope">
                {{ scope.row.expiry | formatDate }}
              </template>
            </el-table-column>
            <el-table-column label="Type Of Document">
              Expired Insurance
            </el-table-column>
            <el-table-column label="Actions">
              <template slot-scope="scope">
                <a :href="scope.row.url">
                  <el-button size="mini" icon="el-icon-download"></el-button>
                </a>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
     <el-dialog
        id="document"
        :visible.sync="dialogFormVisible"
        destroy-on-close
        :close-on-click-modal="false"
      >
       <el-card>
         <AutoDocument
           v-if="!loading"
           :facility="facility"
           :id="id"
           :customer-invoices="customerInvoices"
           :unit-rentals="unitRentals"
           @completed="prelienAnvilCompleted"
         />
       </el-card>
      </el-dialog>
     <el-dialog :title="viewDocumentTitle" :visible.sync="dialogForPdf">
       <img
        v-if="!isPdf && selectedDocument"
        :src="selectedDocument.url"
        :width="500"
        alt="Preview"
      />
       <PreviewPdf v-if='isPdf && selectedDocument' :source="selectedDocument.url" />
     </el-dialog>
  </el-card>
</template>

<script>
import axios from 'axios';
import { startOfMonth, addMonths, isBefore } from 'date-fns';
import AutoDocument from '@/components/AutoDocument';
import PreviewPdf from './PreviewPdf';
import RsAnvilClient from '../api/rsAnvilApi';
import { FEDEX_API, UPLOAD_API, UNIT_RENTAL_API_BASE, LEASING_API } from '../env';
import { getCustomerById } from '../api/customers';
import { createCustomerDocument, updateCustomerDocument, getCustomerDocuments } from '../api/documents';
import getMonthAndYear, { setMoveinToUTC } from '../utils/timing';

const documentTitleMap = {
  PRELIMINARY_LIEN_NOTICE: 'Pre Lien Notice',
  CREDIT_CARD_AUTHORIZATION: 'Credit Card Authorization',
  NOTICE_OF_LIEN: 'Notice of Lien',
};

export default {
  name: 'CustomerDocuments',
  props: {
    ready: { type: Boolean, default: false },
    id: { type: String, required: true },
    facility: { type: String, required: true },
    customerInvoices: { type: Array, default: () => [] },
  },
  computed: {
    viewDocumentTitle() {
      if (!this.selectedDocument) {
        return '';
      }

      return documentTitleMap[this.selectedDocument.type] || 'Other';
    },
    activeLease() {
      const [activeL] = this.customerInvoices.filter(lease => lease && !lease.future);
      return activeL;
    },
  },
  components: { AutoDocument, PreviewPdf },
  data() {
    return {
      shipLabelButtonLoading: false,
      options: '',
      unitRentals: [],
      dialogFormVisible: false,
      customerDocs: [],
      loading: false,
      selectedDocument: null,
      dialogForPdf: false,
      rsCustomer: null,
      isPdf: false,
      activeTab: 'active',
    };
  },
  watch: {
    async facility() {
      this.loading = true;
      await this.getCustomer();
      this.loading = false;
    },
    async id() {
      this.loading = true;
      await this.getCustomer();
      this.loading = false;
    },
  },
  async mounted() {
    this.loading = true;
    await this.getCustomer();
    this.unitRentals = await this.getUnitRentalsByInvoice();
    this.loading = false;
  },
  methods: {
    async addFedExFeeToLeases() {
      // Add $12.00 FedEx shipping fee to prelien and lien leases
      const lease = this.activeLease;
      if (!lease) { return; }
      if (!['LIEN', 'PRE_LIEN'].includes(lease.status)) { return; }

      const amount = 16.00;
      const documentTypeMap = {
        LIEN: 'Notice of Lien Sale',
        PRE_LIEN: 'Preliminary Lien Notice',
      };

      const today = new Date();
      const firstOfNextMonth = startOfMonth(addMonths(today, 1));

      const endDate = new Date(lease.end);
      // eslint-disable-next-line no-nested-ternary
      const firstOfMoveoutMonth = lease.end && isBefore(
        startOfMonth(endDate), firstOfNextMonth)
        ? startOfMonth(endDate) : null;
      const period = setMoveinToUTC(firstOfMoveoutMonth || firstOfNextMonth);
      const dueDate = setMoveinToUTC(firstOfMoveoutMonth || firstOfNextMonth);

      const newLineItem = {
        createdAt: new Date(),
        period,
        description: `FedEx shipping fee for ${documentTypeMap[lease.status]} Packet - ${getMonthAndYear(period)}`,
        amount,
        accountBalance: (lease.amount + amount) - lease.amountPaid,
        dueDate,
        type: 'CREDIT',
        details: 'FEES',
        chargeType: 'FEES',
        feeType: 'Auction Fee',
      };

      await axios.put(
        // eslint-disable-next-line no-underscore-dangle
        `${LEASING_API}/v1/${this.facility}/${lease._id}/add/line/item`,
        {
          newLineItem,
        },
        {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        },
      );

      this.$message.success({
        message: 'Added FedEx shipping fee to leases.',
      });
    },
    async getUnitRentalsByInvoice() {
      if (this.activeLease) {
        const response = await axios.get(`${UNIT_RENTAL_API_BASE}`, {
          params: {
            unitInvoice: this.activeLease._id,
          },
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        return response.data.result;
      }
      return [];
    },
    async getCustomer() {
      this.loading = true;
      const customerResponse = await getCustomerById(this.id, this.facility, this.$keycloak.token);
      const customerDocsResponse = await getCustomerDocuments(this.id, false, this.$keycloak.token);

      this.rsCustomer = customerResponse;
      this.rsCustomer.docs = customerDocsResponse.data;
      this.rsCustomer.archivedDocs = customerResponse.archivedInsurances;

      // eslint-disable-next-line no-restricted-syntax
      for (const doc of customerDocsResponse.data) {
        const documentDownloaded = (doc && doc.url);
        if (!documentDownloaded) {
          if (doc && doc.documentEid !== undefined) {
            // eslint-disable-next-line no-await-in-loop
            const response = await this.getDocumentSignStatus(doc);

            if (response.data && response.data.data && response.data.data.etchPacket) {
              doc.statusData = response.data.data.etchPacket;
            }

            if (doc.statusData.status === 'completed') {
              // eslint-disable-next-line no-await-in-loop
              const agreementBuffer = await this.getDocumentBuffer(doc);

              const formData = new FormData();
              const buffer = new Uint8Array(agreementBuffer.data.data);
              const blob = new Blob([buffer], { type: 'application/pdf' });
              formData.append('document', blob, `${doc.documentEid}.pdf`);

              // eslint-disable-next-line no-await-in-loop
              const response = await axios.post(UPLOAD_API, formData, {
                headers: {
                  Authorization: `Bearer ${this.$keycloak.token}`,
                  'Content-Type': 'multipart/form-data',
                },
              });

              updateCustomerDocument(this.id, doc._id, { url: response.data.url }, true, this.$keycloak.token);
            }
          }
        }
      }
      this.customerDocs = customerDocsResponse.data;
      this.loading = false;
    },
    async getDocumentSignStatus(doc) {
      const rsClient = new RsAnvilClient(this.$keycloak.token);
      const response = await rsClient.getSignersStatus(doc.documentEid);
      return response;
    },
    async getDocumentBuffer(doc) {
      const rsClient = new RsAnvilClient(this.$keycloak.token);
      const agreementBuffer = await rsClient.downloadAgreement(doc.documentGroupEid);

      return agreementBuffer;
    },
    async createFedExLabel() {
      if (!this.facility) {
        this.$message.error({
          title: 'Error',
          message: 'Please select a facility to create a FedEx label',
        });
        return;
      }
      if (!['us', 'ca'].includes(this.rsCustomer.country)) {
        this.$message.error({
          title: 'Error',
          message: 'FedEx label generation is only supported for US and CA.',
        });
        return;
      }
      this.shipLabelButtonLoading = true;
      try {
        const response = await axios.post(`${FEDEX_API}/${this.facility}/${this.id}`, {
          packagingType: 'FEDEX_ENVELOPE',
          pickupDetail: {
            latestPickupDateTime: new Date(), // '2023-07-12T06:04:02.438Z',
            readyPickupDateTime: new Date(), // '2023-07-12T10:04:02.438Z',
          },
          pickupType: 'DROPOFF_AT_FEDEX_LOCATION',
          totalWeight: 0.5,
          recipients: [{
            address: {
              streetLines: [
                this.rsCustomer.address.length > 33
                  ? this.rsCustomer.address.substring(0, 33) : this.rsCustomer.address,
                ...(
                  // eslint-disable-next-line no-nested-ternary
                  !this.rsCustomer.address2 ? [] : this.rsCustomer.address2 && this.rsCustomer.address2.length > 33
                    ? [this.rsCustomer.address2.substring(0, 33)] : [this.rsCustomer.address2]
                ),
              ],
              city: this.rsCustomer.city,
              stateOrProvinceCode: this.rsCustomer.state,
              postalCode: this.rsCustomer.zip,
              countryCode: this.rsCustomer.country ? this.rsCustomer.country.toUpperCase() : '',
              residential: false,
              // streetLines: [
              //   `${this.$root.state.facilityData.addressInfo.addressLine1}`,
              //   // `${this.$root.state.facilityData.addressInfo.addressLine2}`,
              // ],
              // city: this.$root.state.facilityData.addressInfo.city,
              // stateOrProvinceCode: this.$root.state.facilityData.addressInfo.state,
              // postalCode: this.$root.state.facilityData.addressInfo.pinCode,
              // countryCode: 'US',
              // residential: false,
            },
            contact: {
              personName: this.rsCustomer.primaryContact.name,
              emailAddress: this.rsCustomer.email,
              // phoneExtension: '91',
              phoneNumber: this.rsCustomer.phone,
              companyName: this.getCompanyName(),
            },
          }],
          requestedPackageLineItems: [{
            sequenceNumber: 1,
            weight: {
              units: 'LB',
              value: 0.5,
            },
          }],
          serviceType: 'FEDEX_2_DAY',
          labelSpecification: {
            labelFormatType: 'COMMON2D',
            labelStockType: 'PAPER_85X11_TOP_HALF_LABEL',
            imageType: 'PDF',
          },
          // labelType: 'COMMON2D, PDF, PAPER_85X11_TOP_HALF_LABEL',
          // shipTimestamp: '',
          shipper: {
            address: {
              streetLines: [
                `${this.$root.state.facilityData.addressInfo.addressLine1}`,
                // `${this.$root.state.facilityData.addressInfo.addressLine2}`,
              ],
              city: this.$root.state.facilityData.addressInfo.city,
              stateOrProvinceCode: this.$root.state.facilityData.addressInfo.state,
              postalCode: this.$root.state.facilityData.addressInfo.pinCode,
              countryCode: this.$root.state.facilityData.addressInfo.country ? this.$root.state.facilityData.addressInfo.country.toUpperCase() : '',
              residential: false,
            },
            contact: {
              personName: this.$keycloak.tokenParsed.name,
              emailAddress: this.$keycloak.tokenParsed.email,
              // phoneExtension: '91',
              phoneNumber: this.$root.state.facilityData.manager.phone,
              companyName: 'ReadySpaces',
            },
          },
          shippingChargesPayment: {
            paymentType: 'SENDER',
          },
        }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });

        if (response.data.errors) {
          this.$message.error({
            message: `${response.data.errors[0].message}
            ${response.data.errors[0].parameterList ? response.data.errors[0].parameterList[0].value : ''}
            ${response.data.errors[0].parameterList ? response.data.errors[0].parameterList[0].key : ''}
            ${response.data.errors[0].code}
            `,
          });
        }

        if (response.data && response.data.output) {
          const [createdShipment] = response.data.output.transactionShipments;
          const encoded = createdShipment.pieceResponses[0].packageDocuments[0].encodedLabel;
          console.log(encoded);
          const pdfFileName = createdShipment.pieceResponses[0].masterTrackingNumber;
          console.log(pdfFileName);

          const formData = new FormData();
          const buffer = Buffer.from(encoded, 'base64');
          const blob = new Blob([buffer], { type: 'application/pdf' });
          formData.append('document', blob, `${pdfFileName}.pdf`);

          const uploadResponse = await axios.post(UPLOAD_API, formData, {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
              'Content-Type': 'multipart/form-data',
            },
          });

          const fedexDoc = {
            url: uploadResponse.data.url,
            type: 'FEDEX',
            createdAt: new Date(),
            createdBy: this.$keycloak.tokenParsed.email,
          };

          try {
            await createCustomerDocument(this.id, fedexDoc, true, this.$keycloak.token);
            this.rsCustomer.docs.push(fedexDoc);
          } catch (e) {
            this.$message.error({
              title: 'Error',
              message: 'There was an error uploading the document.',
            });
            this.getCustomer();
          }

          try {
            await this.addFedExFeeToLeases();
          } catch (err) {
            this.$message.error({
              title: 'Error',
              message: 'There was an error Adding the line item for FedEx shipping fee.',
            });
          }
        }
      } catch (err) {
        this.$message.error({
          message: err.response.data,
        });
      }
      this.shipLabelButtonLoading = false;
    },
    getCompanyName() {
      const maxLength = 33;
      const companyName = this.rsCustomer.company;

      if (companyName.length > maxLength) {
        const truncatedName = companyName.substring(0, maxLength);
        return truncatedName;
      }
      return companyName;
    },
    checkForActiveLease() {
      if (this.customerInvoices.length === 0) {
        this.$message.error({
          title: 'Error',
          message: 'No Active Lease for this facility',
        });
        return;
      }
      this.dialogFormVisible = true;
    },
    openPreview(doc) {
      if (doc.url.toLowerCase().endsWith('.pdf')) {
        this.isPdf = true;
      }
      this.selectedDocument = doc;
      this.dialogForPdf = true;
    },
    async prelienAnvilCompleted() {
      this.dialogFormVisible = false;
      await this.getCustomer();
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/CustomerDocuments.vue